import axios from 'axios';
import { API_BASE_URL, API_KEY } from '../config';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 1800000, // 30 minutes for large file uploads and analysis processing
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
  },
  withCredentials: true, // Include cookies for authentication
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Ensure API key is always present
    if (!config.headers['x-api-key']) {
      config.headers['x-api-key'] = API_KEY;
    }
    
    // Add auth token if available (check both user and admin tokens)
    const userToken = localStorage.getItem('accessToken');
    const adminToken = localStorage.getItem('adminAccessToken');
    const token = userToken || adminToken;
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log request in development
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('API Request:', {
    //     method: config.method?.toUpperCase(),
    //     url: config.url,
    //     headers: {
    //       'x-api-key': config.headers['x-api-key'] ? '***' : 'missing',
    //       'Authorization': config.headers.Authorization ? 'Bearer ***' : 'none',
    //     },
    //     data: config.data,
    //   });
    // }
    
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Log response in development
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('API Response:', {
    //     url: response.config.url,
    //     status: response.status,
    //     data: response.data,
    //   });
    // }
    
    return response;
  },
  (error) => {
    // Handle errors
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      if (status === 401) {
        // Unauthorized - clear both tokens and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('adminAccessToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('adminData');
        sessionStorage.removeItem('selectedPatient');
        
        // Redirect to login page
        // Check if we're already on login page to avoid redirect loop
        if (window.location.pathname !== '/login') {
          // Use window.location for redirect since we're outside React Router context
          window.location.href = '/login';
        }
      } else if (status === 403) {
        console.error('Forbidden - You do not have permission');
      } else if (status === 404) {
        console.error('Not Found - The resource does not exist');
      } else if (status >= 500) {
        console.error('Server Error - Please try again later');
      }
      
      // Return formatted error
      return Promise.reject({
        message: data?.message || 'An error occurred',
        status,
        errors: data?.errors,
      });
    } else if (error.request) {
      // Request made but no response
      console.error('Network Error:', error.message);
      
      // Check if it's a timeout error
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        return Promise.reject({
          message: 'Request timed out after 30 minutes. The analysis is still processing on the server. Please check the status later.',
          status: 0,
          isTimeout: true,
        });
      }
      
      return Promise.reject({
        message: 'Network error. Please check your connection.',
        status: 0,
      });
    } else {
      // Something else happened
      console.error('Error:', error.message);
      return Promise.reject({
        message: error.message || 'An unexpected error occurred',
        status: 0,
      });
    }
  }
);

export default apiClient;

